import React, { useState, useEffect, useMemo } from "react";
import Navbar from "../Components/Navbar";
import { useNavigate } from "react-router-dom";
import HelpMessage from "../Components/HelpMessage";
import axios from "axios";
import SearchBar from "../Components/SearchBar";
import { fetchSupplierDataForHome } from "../Components/Search";

const Home = () => {
  const [userData, setUserData] = useState(null);
  const [volumeData, setVolumeData] = useState([]);
  const [proceduresData, setProceduresData] = useState([]);
  const [testMapping, setTestMapping] = useState([]);
  const [reportSettings, setReportSettings] = useState(null);
  const navigate = useNavigate();

  const instanceSettings = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("instance_settings") || "{}") || {};
    } catch (error) {
      console.error("Failed to parse instance settings", error);
      return {};
    }
  }, []);

  const userRole = localStorage.getItem("role") || "";
  const isAdminUser = userRole.toLowerCase().includes("admin");

  const hydrateFromPayload = (payload) => {
    if (!payload) {
      return;
    }

    setUserData(payload.userData);
    setVolumeData(payload.volumeData || []);
    setProceduresData(payload.resultProcedure || []);

    const mapping = payload.resultProcedure?.testMapping || [];
    setTestMapping(Array.isArray(mapping) ? mapping : []);
    setReportSettings(payload.reportSettings);
  };

  const getCachedAdminPayload = (supplierId) => {
    if (!supplierId) return null;
    try {
      const raw = sessionStorage.getItem("admin_supplier_payload");
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (parsed?.supplierId !== supplierId) {
        return null;
      }
      return parsed.payload || null;
    } catch (error) {
      console.warn("Failed to parse cached admin payload:", error);
      return null;
    }
  };

  const cacheAdminPayload = (supplierId, payload) => {
    if (!supplierId || !payload) {
      return;
    }
    try {
      sessionStorage.setItem(
        "admin_supplier_payload",
        JSON.stringify({
          supplierId,
          payload,
          cachedAt: Date.now()
        })
      );
    } catch (error) {
      console.warn("Unable to cache admin payload:", error);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const userRole = localStorage.getItem("role");
        const isAdmin = userRole && userRole.includes("admin");
        let supplierData = localStorage.getItem("currentUser");

        // For admin users, if no supplier is selected, get the first available supplier
        // This matches the old .NET app behavior where Home sets ViewingUserId from first result
        if (isAdmin && !supplierData) {
          try {
            const token = localStorage.getItem("token");
            const response = await axios.get(
              `${process.env.REACT_APP_API_BASEURL}/supplierNames`,
              {
                headers: {
                  "Content-Type": "application/json",
                  Authorization: `Bearer ${token}`,
                },
              }
            );
            const suppliers = response.data || [];
            if (suppliers.length > 0) {
              supplierData = suppliers[0]?.supplier?.trim() || "";
              const supplierName = suppliers[0]?.name?.trim() || suppliers[0]?.supplier || "";
              
              // Set the supplier in localStorage so all pages use it
              localStorage.setItem("currentUser", supplierData);
              localStorage.setItem("supplierName", supplierName);
              
              // Dispatch event to notify other components
              window.dispatchEvent(
                new CustomEvent("supplierContextChanged", {
                  detail: {
                    supplier: supplierData,
                    name: supplierName
                  }
                })
              );
            }
          } catch (error) {
            console.error("Error fetching supplier data:", error);
          }
        }

        let response;
        if (isAdmin && supplierData) {
          const cached = getCachedAdminPayload(supplierData);
          if (cached) {
            hydrateFromPayload(cached);
          }

          response = await fetchSupplierDataForHome(supplierData);
          cacheAdminPayload(supplierData, response);
        } else {
          response = await fetchAllData();
        }

        hydrateFromPayload(response);
      } catch (error) {
        console.error("Error fetching data for Home:", error);
        const supplierData = localStorage.getItem("currentUser");
        const cached = getCachedAdminPayload(supplierData);
        if (cached) {
          hydrateFromPayload(cached);
        }
      }
    };

    fetchData();
  }, []);

  const handleCardClick = (monthData) => {
    const userRole = localStorage.getItem("role");
    const isAdmin = userRole && userRole.includes("admin");

    if (isAdmin && monthData) {
      monthData.supplier = localStorage.getItem("currentUser")?.trim();
    }

    localStorage.setItem("currentCard", JSON.stringify(monthData));
    const volumeValue = volumeByMonthYear[`${getMonthName(monthData.month)} ${monthData.year}`] || 0;
    localStorage.setItem("volumeValue", volumeValue);
    navigate(`/Results`);
  };

  const handleNotesClick = (month, year, event) => {
    if (event) {
      event.stopPropagation();
    }
    navigate(`/Field-Notes/${month}-${year}`);
  };

  const fetchAllData = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASEURL}/api/Home/data`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching data:", error);
      throw error;
    }
  };

  const getMonthName = (monthNumber) => {
    const months = [
      "January", "February", "March", "April", "May", "June",
      "July", "August", "September", "October", "November", "December",
    ];
    return months[monthNumber - 1];
  };

  const volumeByMonthYear = {};
  volumeData.forEach((entry) => {
    const monthName = getMonthName(entry.month);
    const year = entry.year;
    const key = `${monthName} ${year}`;
    if (!volumeByMonthYear[key]) {
      volumeByMonthYear[key] = 0;
    }
    const numericVolume = Number(entry.vol ?? entry.Vol ?? entry.volume ?? 0);
    volumeByMonthYear[key] += Number.isNaN(numericVolume) ? 0 : numericVolume;
  });

  const getDisplayColumns = () => {
    const mappedColumns = (Array.isArray(testMapping) ? testMapping : [])
      .filter(({ code }) => !!code)
      .map(({ code, label }) => {
        const normalizedCode = code.trim().toUpperCase();
        const reportEntry =
          reportSettings?.[normalizedCode] ||
          reportSettings?.[normalizedCode.toLowerCase()];
        const isEnabled =
          reportEntry && typeof reportEntry === "object"
            ? reportEntry.enabled !== false
            : true;

        return {
          key: normalizedCode,
          label: reportEntry?.label || label || normalizedCode,
          enabled: isEnabled,
        };
      })
      .filter(({ enabled }) => enabled);

    if (mappedColumns.length > 0) {
      return mappedColumns.slice(0, 20);
    }

    if (!reportSettings) {
      return [
        { key: 'Test1', label: 'Test 1' },
        { key: 'Test2', label: 'Test 2' },
        { key: 'Test3', label: 'Test 3' },
        { key: 'Test4', label: 'Test 4' },
        { key: 'Test5', label: 'Test 5' },
        { key: 'Test6', label: 'Test 6' },
        { key: 'Test7', label: 'Test 7' },
        { key: 'Test8', label: 'Test 8' },
        { key: 'Test9', label: 'Test 9' },
        { key: 'Test10', label: 'Test 10' }
      ];
    }

    return Object.keys(reportSettings)
      .filter((key) => {
        const entry = reportSettings[key];
        if (entry && typeof entry === "object" && entry.enabled !== undefined) {
          return Boolean(entry.enabled);
        }
        return true;
      })
      .map((key) => {
        const normalizedKey = key.trim().toUpperCase();
        const mappingEntry = (testMapping || []).find(
          ({ code }) => code && code.toUpperCase() === normalizedKey
        );
        const entryLabel =
          mappingEntry?.label ||
          reportSettings[key]?.label ||
          normalizedKey;

        return {
          key: normalizedKey,
          label: entryLabel
        };
      });
  };

  const formatMetricValue = (value) => {
    if (value === null || value === undefined) {
      return '—';
    }

    if (typeof value === 'number') {
      return Number.isNaN(value) ? '—' : value.toFixed(2);
    }

    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (!trimmed) {
        return '—';
      }
      const numeric = Number(trimmed.replace(/[^0-9.-]+/g, ''));
      return Number.isNaN(numeric) ? trimmed : numeric.toFixed(2);
    }

    return value;
  };

  const formatVolume = (value) => {
    const numeric = Number(value);
    if (Number.isNaN(numeric)) {
      return '—';
    }
    return `${numeric.toLocaleString()} L`;
  };

  const formatCurrency = (value) => {
    const numeric = Number(value);
    if (Number.isNaN(numeric)) {
      return '—';
    }
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: 'EUR'
    }).format(numeric);
  };

  const parsePaymentValue = (value) => {
    if (typeof value === 'number') {
      return value;
    }
    if (typeof value === 'string') {
      const numeric = Number(value.replace(/[^0-9.-]+/g, ''));
      return Number.isNaN(numeric) ? 0 : numeric;
    }
    return 0;
  };

  const getSupplierName = () => {
    let fullName = null;
    const records = Array.isArray(userData) ? userData : userData ? [userData] : [];
    const candidateFields = [
      'USERNAME',
      'username',
      'SuppName',
      'SupplierName',
      'supplier_name',
      'FullName',
      'full_name',
      'name',
      'supplier'
    ];

    for (const record of records) {
      if (!record || typeof record !== 'object') continue;
      for (const field of candidateFields) {
        const value = record[field];
        if (typeof value === 'string' && value.trim().length > 0) {
          fullName = value.trim();
          break;
        }
      }
      if (fullName) break;
    }

    // If no name found in userData, check localStorage
    if (!fullName) {
      const stored = localStorage.getItem('supplierName');
      if (stored && stored.trim()) {
        fullName = stored.trim();
      }
    }

    // If we have a full name, extract the first name (first word)
    if (fullName) {
      const firstName = fullName.split(/\s+/)[0];
      if (firstName && firstName.trim().length > 0) {
        return firstName.trim();
      }
    }

    // Fall back to supplier ID if no name available
    const currentUser = localStorage.getItem('currentUser');
    return currentUser ? currentUser.trim() : 'Supplier';
  };

  const renderDataByMonths = () => {
    const records = proceduresData?.recordsets?.[1];
    if (!records || !records.length) {
      return (
        <div className="home-empty">
          No milk quality records available yet.
        </div>
      );
    }

    const displayColumns = getDisplayColumns().slice(0, 8);

    return records.map((monthData, index) => {
      const monthYearKey = `${getMonthName(monthData.month)} ${monthData.year}`;
      const metrics = displayColumns.map((column) => ({
        label: column.label,
        value: formatMetricValue(monthData[column.key])
      }));
      const paymentRaw = monthData.Payment || monthData.payment;
      const paymentNumeric = parsePaymentValue(paymentRaw);
      const showPayment = paymentNumeric > 0;
      const totalVolume = monthData.CollectedVol ?? monthData.collectedvol ?? null;

      return (
        <article
          key={`${monthYearKey}-${index}`}
          className="month-card"
          onClick={() => handleCardClick(monthData)}
          role="button"
          tabIndex={0}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              handleCardClick(monthData);
            }
          }}
        >
          <header className="month-card__header">
            <div>
              <h3>{monthYearKey}</h3>
              <p>
                Volume:{" "}
                {totalVolume !== null
                  ? formatVolume(totalVolume)
                  : formatVolume(volumeByMonthYear[monthYearKey] || 0)}
              </p>
            </div>
            <button
              type="button"
              className="icon-button"
              onClick={(event) => handleNotesClick(monthData.month, monthData.year, event)}
              aria-label={`Open notes for ${monthYearKey}`}
            >
              <i className="uicon-message" />
            </button>
          </header>

          <div className="month-card__metrics">
            {metrics.map(({ label, value }) => (
              <div key={`${monthYearKey}-${label}`} className="metric-tile">
                <span className="metric-label">{label}</span>
                <span className="metric-value">{value}</span>
              </div>
            ))}
          </div>

          {showPayment && (
            <footer className="month-card__footer">
              <span>Payment</span>
              <strong>{formatCurrency(paymentNumeric)}</strong>
            </footer>
          )}

        </article>
      );
    });
  };

  return (
    <div className="customer-layout">
      <Navbar />
      <main className="customer-main">
        <section className="page-hero card">
          <h1 className="page-hero__title">Welcome back, {getSupplierName()}.</h1>
        </section>

        {/* {isAdminUser && (
          <section className="card home-search">
            <SearchBar />
          </section>
        )} */}

        <section className="month-list">
          {renderDataByMonths()}
        </section>

        <div className="home-help">
          <HelpMessage />
        </div>
      </main>
    </div>
  );
};

export default Home;
